/* eslint-disable no-restricted-syntax */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Params, Router } from '@angular/router';

import { Dayjs } from 'dayjs';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, fromEvent, map, merge, Observable, of, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { isString, SDate } from '@summit/core';
import {
  FilterParameter,
  ICrudService,
  IDisplaySchema,
  IProfile,
  PasswordValidation,
  SummitCoreHelpers,
  SummitDisposing,
  SummitIdObject
} from '@summit/models';

import { inject, Injectable } from '@angular/core';
import { CrudCacheItem } from './crud-cache-item';
import { EventLevel } from './event-level.enum';
import { EventLog } from './event-log';
import { EventStatus } from './event-status.enum';
import { HistoryService } from './history';
import { SummitNotificationService } from './notification';
import { SummitAuthService } from './summit-auth-service';

@Injectable()
export class SummitCrudService extends SummitDisposing implements ICrudService {
  readonly stringifyData = false;
  readonly jsonHttpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };
  readonly debug = false;

  protected baseUrl: string;
  protected cache = new Map<string, BehaviorSubject<CrudCacheItem<unknown>>>();
  protected draftCache = new Map<string | number, CrudCacheItem<unknown>>();
  protected activeProfile: IProfile;
  protected http: HttpClient;
  protected router: Router;
  public notificationService: SummitNotificationService; //updated by notification service
  protected history: HistoryService;
  protected authService: SummitAuthService;
  protected messageService: MessageService;

  public online$: Observable<boolean>;

  constructor() {
    super();
    this.messageService = inject(MessageService);
    this.online$ = merge(
      of(window.navigator.onLine),
      fromEvent(window, 'online').pipe(
        tap(() => console.log('online')),
        map(() => true)
      ),
      fromEvent(window, 'offline').pipe(
        tap(() => {
          console.log('offline');
        }),
        map(() => false)
      )
    );
  }

  getDefaultTimePeriod(): { start: Dayjs; end: Dayjs } {
    const begin = SDate.getNormalized();
    return {
      start: begin,
      end: begin
    };
  }

  get<T>(
    object: IDisplaySchema<T> | string,
    idString: string | null = null,
    force: boolean | null = null,
    filter: FilterParameter[] | string | null = null
  ): Observable<CrudCacheItem<T> | null> {
    const debug = false;
    let updateFromServer = force ?? false;
    const api: string | undefined = isString(object) ? object : object.refServerAPI;
    if (!api || api == 'undefined') return of(null);
    //const eventLog = new EventLog(EventLevel.System, `Retrieving ${api}`, EventStatus.InProgress);
    if (debug) console.log(`[CRUD GET] Retrieving ${api}`);
    if (debug) console.log(`[CRUD GET] Debugging ${api}`);
    let cacheKey = api;
    let filterString: string | null = '';
    if (idString) {
      cacheKey += '/' + idString;
    }
    if (debug) console.log(`[CRUD GET] Filter test`, filter);
    if (filter != null) {
      if (isString(filter)) {
        filterString = filter;
        if (!filterString.startsWith('?')) {
          filterString = `?${filterString}`;
        }
      } else {
        const weekRange = filter.find((w) => w.id === 'WeekRange');
        if (weekRange && weekRange.filterData && weekRange.filterData.begin && weekRange.filterData.end) {
          if (debug)
            console.log(`[CRUD GET] Updating WeekRange`, weekRange?.filterData?.begin, weekRange?.filterData?.end);
          filterString = this.getUriFilter(filter, weekRange.filterData.begin, weekRange.filterData.end);
        } else {
          const selWeek = this.getDefaultTimePeriod();
          //console.log(`using week ending in ${selWeek.end.format()}`, selWeek);
          filterString = this.getUriFilter(filter, selWeek['start'], selWeek['end']);
        }
      }
      if (filterString == null) {
        return of(null);
      }
      cacheKey += filterString;
      // Cache key doesn't have all values replaced
      filterString = filterString.replace(`[NOW]`, SDate.new().format());
    }
    if (debug) console.log(`[CRUD GET] Create Filter String of ${filterString}`);

    // eslint-disable-next-line no-console
    //console.debug(`Getting /${cacheKey} with filters:`, filterParameters);
    let cacheItemBS: BehaviorSubject<CrudCacheItem<T>>;
    let updateItem: CrudCacheItem<T>;

    if (this.cache.has(cacheKey)) {
      // Pull from cache
      if (debug)
        console.log(
          `[CRUD GET] Pull from cache ${cacheKey} with time of ${this.cache
            .get(cacheKey)
            ?.getValue()
            .parameters.freshnessDate.format()}`
        );
      cacheItemBS = this.cache.get(cacheKey) as BehaviorSubject<CrudCacheItem<T>>;
      if (
        !this.cache.get(cacheKey)?.getValue().parameters.freshnessDate ||
        this.cache.get(cacheKey)?.getValue().parameters.freshnessDate.isBefore(SDate.new().subtract(15, 'second')) ||
        force
      ) {
        // console.debug(`Fetching from server: Force: ${force}
        // or Freshness: ${this.cache.get(cacheKey).getValue().parameters.freshnessDate}`);
        updateItem = cacheItemBS.getValue();
        updateItem.parameters.isLoading = true;
        updateItem.parameters.freshnessDate = SDate.new(); // used to some back to back queries
        if (debug) console.log(`[CRUD GET] Sending Cache To Observable`, updateItem);
        cacheItemBS.next(updateItem);
        updateFromServer = true;
      }
      /*
                              else {
                                  updateItem = cacheItemBS.getValue();
                                  cacheItemBS.next(updateItem);
                              }
                              */
    } else {
      // Not in Cached
      if (debug) console.log(`[CRUD GET] Not in cache ${cacheKey}`);
      cacheItemBS = new BehaviorSubject<CrudCacheItem<T>>(
        CrudCacheItem.CreatingLoadingDefaultItem<T>(idString, filter)
      );
      // Try to validate it is still empty, if it isn't then use current value
      if (!this.cache.has(cacheKey) || !this.cache.get(cacheKey))
        this.cache.set(cacheKey, cacheItemBS as BehaviorSubject<CrudCacheItem<unknown>>);
      else cacheItemBS = this.cache.get(cacheKey) as BehaviorSubject<CrudCacheItem<T>>;
      updateFromServer = true;
    }

    if (debug) console.log(`[CRUD GET] Should query server ${updateFromServer} - ${cacheKey}`);
    if (updateFromServer) {
      if (debug) console.log(`[CRUD GET] Requesting from server (test) ${cacheKey}`);
      let url = idString
        ? this.router.createUrlTree([this.baseUrl + api + '/' + idString]).toString()
        : this.router.createUrlTree([this.baseUrl + api]).toString();
      if (filterString.length > 0) {
        url += filterString;
      }
      console.debug(`CRUD Service: Getting : ${url}`);
      this.http
        .get<T>(url, this.jsonHttpOptions)
        .pipe(
          catchError((error) => {
            console.log(`[CRUD GET] Error getting data from ${url}`, error);
            if (error.status == 0) {
              console.warn(`[CRUD GET] Connection issue: HTTP Status ${error.status}`);
            } else {
              console.warn(`[CRUD GET] Got Error going to ${url}`, error);
            }
            this.handleError<unknown>('Failed', error);
            return of(undefined);
          })
          // tap((data) => {
          //   console.log('got data', data);
          // })
        )
        .subscribe(async (data) => {
          if (data === undefined) return;
          //console.log(`[${new Date()}][CRUD GET] ${cacheKey} CRUD got data`, data);
          if (debug && this.stringifyData)
            console.log(`[${new Date()}][CRUD GET] ${cacheKey} CRUD got data \n${JSON.stringify(data, null, 2)}`);
          // Cache shouldn't be null here but found the bug in prod.
          if (!this.cache.has(cacheKey) || this.cache.get(cacheKey) == undefined || this.cache.get(cacheKey) == null) {
            const newBsCrudItem = new BehaviorSubject<CrudCacheItem<T>>(
              CrudCacheItem.CreatingLoadingDefaultItem<T>(idString, filter)
            );
            //Trying to make it close to atomic
            if (debug) console.log('[CRUD GET] Creating new cache item', newBsCrudItem, this.cache);
            if (!this.cache.has(cacheKey) || !this.cache.get(cacheKey))
              this.cache.set(cacheKey, newBsCrudItem as BehaviorSubject<CrudCacheItem<unknown>>);
            if (debug) console.log('[CRUD GET] Updated cache', this.cache);
          }

          const bsCrudItem = this.cache.get(cacheKey);
          if (bsCrudItem) {
            const update = bsCrudItem.getValue();
            update.parameters.isLoading = false;
            update.parameters.freshnessDate = SDate.new();
            if (idString === null) {
              update.data = data as T;
            } else {
              update.data = data as unknown as Array<T>;
            }
            update.dataId = uuid();
            bsCrudItem.next(update);
          }
        });
    }
    if (debug) console.log(`[CRUD GET] Current Cached Items: Key = ${cacheKey}`, this.cache);
    if (debug) console.log(`[CRUD GET] Key = ${cacheKey} Value = `, this.cache.get(cacheKey));
    return this.cache.get(cacheKey)?.asObservable() as Observable<CrudCacheItem<T>>;
  }

  getWithCred<T>(
    object: IDisplaySchema<T> | string,
    idString: string,
    passwordValidation: PasswordValidation
  ): Observable<CrudCacheItem<T> | null> {
    const api: string | undefined = isString(object) ? object : object.refServerAPI;
    let cacheKey = api ?? '';
    if (idString) {
      cacheKey += '/' + idString;
    }

    // eslint-disable-next-line no-console
    // console.debug(`Getting /${cacheKey} with filters:`, filterParameters);
    let cacheItemBS: BehaviorSubject<CrudCacheItem<T>>;
    let updateItem: CrudCacheItem<T>;

    if (this.cache.has(cacheKey)) {
      // Pull from cache
      // console.log(`Pull from cache ${cacheKey} with time of ${this.cache.get(cacheKey).getValue().parameters.freshnessDate}`);
      cacheItemBS = this.cache.get(cacheKey) as BehaviorSubject<CrudCacheItem<T>>;
      if (
        !this.cache.get(cacheKey)?.getValue().parameters.freshnessDate ||
        this.cache.get(cacheKey)?.getValue().parameters.freshnessDate.isBefore(SDate.new().subtract(5, 'second'))
      ) {
        updateItem = cacheItemBS.getValue();
        updateItem.parameters.isLoading = true;
        // console.log(`Sending to observable`, updateItem);
        cacheItemBS.next(updateItem);
      }
    } else {
      // Not in Cached
      // console.log(`Not in cache ${cacheKey}`);
      cacheItemBS = new BehaviorSubject<CrudCacheItem<T>>(CrudCacheItem.CreatingLoadingDefaultItem<T>(idString, null));
      //Trying to make it close to atomic
      if (!this.cache.has(cacheKey) || !this.cache.get(cacheKey))
        this.cache.set(cacheKey, cacheItemBS as BehaviorSubject<CrudCacheItem<unknown>>);
      else cacheItemBS = this.cache.get(cacheKey) as BehaviorSubject<CrudCacheItem<T>>;
    }

    // console.log(`Requesting from server ${cacheKey}`);
    const url = idString
      ? this.router.createUrlTree([this.baseUrl + api + '/' + idString]).toString()
      : this.router.createUrlTree([this.baseUrl + api]).toString();
    // eslint-disable-next-line no-console
    console.debug(`CRUD Service: Getting : ${url}`);
    this.http.post<T>(url, passwordValidation, this.jsonHttpOptions).subscribe(
      // No Unsubscribe
      (data) => {
        //console.log(`${cacheKey} CRUD got data`, data);
        const bsCrudItem = this.cache.get(cacheKey);
        if (bsCrudItem) {
          const update = bsCrudItem.getValue();
          update.parameters.isLoading = false;
          update.parameters.freshnessDate = SDate.new();
          if (idString === null) {
            update.data = data as T;
          } else {
            update.data = data as unknown as Array<T>;
          }
          update.dataId = uuid();
          bsCrudItem.next(update);
        }
      },
      (error) => console.error('Failed', error)
    );
    // console.log('Current Cached Items', this.cache);
    return this.cache.get(cacheKey)?.asObservable() as Observable<CrudCacheItem<T>>;
  }

  getUriFilter(filter: FilterParameter[], begin: Dayjs, end: Dayjs): string | null {
    let filterString = SummitCoreHelpers.GenerateQueryString(filter, this.activeProfile);
    if (filterString == null) {
      return null;
    }

    //console.log(`filterString: ${filterString}`)
    if (filterString.indexOf('[') >= 0) {
      //console.warn(`Using Injecting date time`, begin, end);
      filterString = filterString.replace('[begin]', SDate.newUtc(begin).format('YYYY-MM-DD'));
      filterString = filterString.replace('[end]', SDate.newUtc(end).format('YYYY-MM-DD'));
      //console.warn(`Using EndTime of ${SDate.new(end).add(1, 'day').subtract(1, 'millisecond').format('YYYY-MM-DD[T]HH:mm:ss.SSS[%2B]00:00')}`);
      //With Timezone
      filterString = filterString.replace('[beginTime]', SDate.newUtc(begin).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'));
      //filterString = filterString.replace('[beginTime]', SDate.newUtc(begin).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'));
      filterString = filterString.replace(
        '[endTime]',
        SDate.newUtc(end).add(1, 'day').subtract(1, 'millisecond').format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
      );
      filterString = filterString.replace('[endTimeFixed]', SDate.newUtc(end).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'));
    }
    return filterString;
  }

  create<T>(
    api: string,
    name: string,
    crudObject: Partial<T> | FormData | unknown,
    requestHttpOptions: object | null = null
  ): Observable<T | undefined> {
    requestHttpOptions ??= this.jsonHttpOptions;
    const newEvent = EventLog.NewEventFrom(EventLevel.Informational, `Saving : ${name}`, EventStatus.InProgress);
    //this.notificationService.add(newEvent); //stopping dialogs call create to close (maybe)
    const url = this.router.createUrlTree([this.baseUrl + api]).toString();

    // Tag this as Adding in the Index
    const indexKey = api;
    if (crudObject instanceof SummitIdObject) {
      const idString = crudObject.id?.toString();
      if (idString) this.flagPendingAction(indexKey, 'add', idString, crudObject);
    }

    // eslint-disable-next-line no-console
    console.debug(`Saving to ${url}`); //, requestHttpOptions);
    //if (this.debug)
    //console.debug(`Saving to ${url} with the message: ${JSON.stringify(crudObject, null, 2)}`, crudObject);
    return this.http.post<T>(url, crudObject, requestHttpOptions).pipe(
      tap((_) => {
        newEvent.status = EventStatus.Complete;
        //this.notificationService.update(newMessage);
        this.updateFreshness(indexKey);
      })
    );
  }

  addToLocalCache<T>(cacheKey: string, object: T) {
    if (this.cache.has(cacheKey)) {
      const value: CrudCacheItem<T> = this.cache.get(cacheKey)?.value as CrudCacheItem<T>;
      //console.log(`Adding to`, value)
      if (Array.isArray(value.data)) value.data.push(object);
      value.dataId = uuid();
      this.cache.get(cacheKey)?.next(value);
    }
  }

  edit<T>(api: string, idString: string, crudObject: T, requestHttpOptions: object | null = null): Observable<T> {
    // Tag this as Editing in the Index
    requestHttpOptions ??= this.jsonHttpOptions;
    const indexKey = api;
    const itemKey = idString != null ? indexKey + '/' + idString : indexKey;
    const url = this.router.createUrlTree([this.baseUrl + itemKey]).toString();

    this.flagPendingAction(indexKey, 'edit', idString, crudObject);

    if (this.debug && this.stringifyData) console.log(`CRUD Putting : ${url}\n${JSON.stringify(crudObject, null, 2)}`);
    return this.http.put<T>(url, crudObject, requestHttpOptions).pipe(
      tap((_) => {
        // this.notificationService.add('Edited')
        this.updateFreshness(indexKey);
      })
    );
  }

  delete(api: string, idString: string, name: string, filterParameters?: FilterParameter[]): Observable<unknown> {
    let filterString: string | null = '';
    const url = this.router.createUrlTree([this.baseUrl + api + '/' + idString]).toString();
    const eventLog = EventLog.NewEventFrom(EventLevel.Informational, `Deleting : ${name}`, EventStatus.InProgress);
    this.notificationService.add(eventLog);
    // Tag this as deleting in the Index
    let indexKey = api;
    const itemKey = indexKey + '/' + idString;
    //console.log(`Deleting Crud Key: ${itemKey}`);
    if (filterParameters != null) {
      // Cache key doesn't have all values replaced
      filterString = SummitCoreHelpers.GenerateQueryString(filterParameters, this.activeProfile);
      if (filterString == null) {
        return of(undefined);
      }
      indexKey += filterString;
    }

    this.flagPendingAction(indexKey, 'delete', idString);

    return this.http.delete(url, this.jsonHttpOptions).pipe(
      tap((_) => {
        eventLog.status = EventStatus.Complete;
        this.notificationService.update(eventLog);
        this.removeItemFromCache(itemKey);
        //* Update anything that is still Fresh
        this.updateFreshness(indexKey);
        //* Remove from history
        this.history.remove(itemKey);
      }),
      catchError(this.handleError<unknown>('Failed', eventLog))
    );
  }

  protected removeItemFromCache(itemKey: string) {
    if (this.cache.has(itemKey)) {
      this.cache.delete(itemKey);
    }
  }

  flagPendingAction<T>(rootUrl: string, actionString: string, idString: string, crudObject?: T) {
    const regexpMatcher = new RegExp(`^${rootUrl}[?\s]`);
    Array.from(this.cache.keys())
      .filter((key) => regexpMatcher.test(key))
      .forEach((key) => {
        const updateItem = this.cache.get(key)?.getValue() as CrudCacheItem<T>;
        switch (actionString) {
          case 'add':
            if (!updateItem.parameters.addedIds) {
              updateItem.parameters.addedIds = new Array<string>();
            }
            updateItem.parameters.addedIds.push(idString);
            if (Array.isArray(updateItem.data) && crudObject) {
              updateItem.data.push(crudObject);
            }
            break;
          case 'edit':
            if (!updateItem.parameters.modifiedIds) {
              updateItem.parameters.modifiedIds = new Array<string>();
            }
            updateItem.parameters.modifiedIds.push(idString);
            break;
          case 'delete':
            if (!updateItem.parameters.removedIds) {
              updateItem.parameters.removedIds = new Array<string>();
            }
            updateItem.parameters.removedIds.push(idString);
            break;
          default:
            break;
        }
        updateItem.dataId = uuid();
        const value = this.cache.get(key);
        if (value) value.next(updateItem);
      });
  }

  updateFreshness(rootUrl: string, overrideDate?: boolean) {
    overrideDate ??= false;
    const regexpMatcher = new RegExp(`^${rootUrl}[?\s]`);
    const stillFresh = SDate.new().subtract(15, 'second');
    Array.from(this.cache.keys())
      .filter(
        (key) =>
          regexpMatcher.test(key) &&
          (overrideDate || this.cache.get(key)?.value.parameters.freshnessDate.isAfter(stillFresh))
      )
      .forEach((key) => {
        //console.log(`Refreshing key: ${key} in `, this.cache)
        const updateItem = this.cache.get(key)?.getValue() as CrudCacheItem<unknown>;
        this.refresh(rootUrl, null, updateItem.parameters.filterParameters);
        /*
                                        if(actionString && idString){
                                            switch(actionString){
                                                case 'add':
                                                    const entryIndex = updateItem.parameters.addedIds.indexOf(idString);
                                                    if (entryIndex >= 0) {
                                                        updateItem.parameters.addedIds.splice(entryIndex, 1);
                                                    }
                                                break;
                                                default:
                                                    break;
                                            }
                                        }
                                        */
      });
  }

  async refresh<T>(
    object: IDisplaySchema<T> | string,
    idString: string | null = null,
    filterParameters: FilterParameter[] | string | null = null
  ) {
    const debug = false;
    if (debug) {
      console.log(`[CRUD.Refresh] Refreshing Key: ${object?.toString()}, Id: ${idString}`, object, filterParameters);
    }
    this.get(object, idString, true, filterParameters);
  }

  private getUrlKey<T>(
    object: string | IDisplaySchema<T>,
    idString: string | null,
    filterParameters: FilterParameter[] | null = null
  ): string | undefined {
    const api = isString(object) ? object : object.refServerAPI;
    let cacheKey = api;
    let filterString: string | null = '';
    if (idString) {
      cacheKey += '/' + idString;
    }
    if (filterParameters != null) {
      // Cache key doesn't have all values replaced
      filterString = SummitCoreHelpers.GenerateQueryString(filterParameters, this.activeProfile);
      if (filterString == null) {
        return undefined;
      }
      cacheKey += filterString;
    }
    return cacheKey;
  }

  markStale(root: string) {
    const regexpMatcher = new RegExp(`^${root}`);
    //console.log(`Marking Stale: ${root}`, this.cache.keys());
    Array.from(this.cache.keys())
      .filter((key) => regexpMatcher.test(key))
      .forEach((key) => {
        //console.log(`Marking Stale Key: ${key}`);
        const updateItem = this.cache.get(key)?.getValue();
        if (updateItem) {
          updateItem.parameters.freshnessDate = SDate.new('1/1/2000', 'M/D/YYYY');
          this.cache.get(key)?.next(updateItem);
        }
      });
  }

  clear<T>(
    object: IDisplaySchema<T> | string | undefined = undefined,
    idString: string | null = null,
    filterParameters: FilterParameter[] | null = null
  ) {
    const debug = false;
    if (object) {
      if (debug) {
        console.log(`[CRUD.Refresh] Clearing Key: ${object.toString()}, Id: ${idString}`);
      }
      const key = this.getUrlKey(object, idString, filterParameters);
      if (key == null) return;
      this.removeItemFromCache(key);
    } else {
      if (debug) {
        console.log(`[CRUD.Refresh] Clearing All Keys:`);
      }
      const cacheKeys = Array.from(this.cache.keys());
      cacheKeys.forEach((key: string) => {
        const bs = this.cache.get(key);
        if (bs) {
          const updateItem = bs.getValue();
          if (updateItem.data && Array.isArray(updateItem.data)) {
            updateItem.data.length = 0;
          }
          if (updateItem.parameters && updateItem.parameters.addedIds) {
            updateItem.parameters.addedIds.length = 0;
          }
          if (updateItem.parameters && updateItem.parameters.modifiedIds) {
            updateItem.parameters.modifiedIds.length = 0;
          }
          if (updateItem.parameters && updateItem.parameters.removedIds) {
            updateItem.parameters.removedIds.length = 0;
          }
          if (updateItem.parameters && updateItem.parameters.freshnessDate) {
            updateItem.parameters.freshnessDate = SDate.new('1/1/2000', 'M/D/YYYY');
          }
          updateItem.parameters.isLoading = false;
          // console.log(`Updating with Clear`, updateItem);
          bs.next(updateItem);
          if (bs.observed && this.authService.isLoggedIn()) {
            const regexpMatcher = RegExp(`^([^\?]*)[\?]?.*$`, 'g');
            const rootUrl = regexpMatcher.exec(key);
            if (rootUrl != null && rootUrl.length > 1) {
              //console.log(`[CRUD.Refresh] Updating Key: ${key} via updating ${rootUrl[1]}`);
              this.updateFreshness(rootUrl[1], true);
            }
          } else {
            //console.log(`[CRUD.Refresh] Cleared Key: ${key} which is being observed: ${bs.observed}`);
          }
        }
      });
    }
  }

  public updateTitle(template: string, value: string, prefix: string | null = null) {
    this.history.updateTitle(template, value, prefix);
  }

  getDraft<T>(id: string | number): CrudCacheItem<T> {
    return this.draftCache.get(id) as CrudCacheItem<T>;
  }
  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  protected handleError<T>(operation = 'operation', eventLog: EventLog, result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      if (error.status == 0) {
        this.messageService.add({
          key: 'notification',
          severity: 'warn',
          summary: `Sorry, we having issues connecting to EBO Pro.`,
          closable: true,
          life: 5000
        });
        return of(result as T);
      }

      // TODO: send the error to remote logging infrastructure
      console.error(`Error status code ${error.status} at ${error.url}`); // log to console instead
      //console.error(`Error status code ${error.status} at ${error.url}`, error); // log to console instead

      // TODO: better job of transforming error for user consumption
      eventLog.eventLevel = EventLevel.Alert;
      eventLog.summary += `\n ${operation} failed with status: ${error.status} and message: ${error.error}`;
      eventLog.status = EventStatus.Failed;
      this.notificationService.update(eventLog);

      if (error.status == 400) {
        //console.log('Error deleting item', error);
        this.messageService.add({
          key: 'notification',
          severity: 'error',
          summary: `Sorry, we received the following error:\n${error.error.message ?? error.error}`,
          closable: true,
          life: 5000
        });
      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /*
   *
   * Draft Section
   *
   */
  addDraft<T extends SummitIdObject>(object: T): Params {
    const newCacheItem = new CrudCacheItem({
      data: object,
      dataId: uuid(),
      draft: true
    });
    const stringId = object.id?.toString() ?? '';
    this.draftCache.set(stringId, newCacheItem);
    return { draftId: object.id };
  }
  updateDraft<T extends SummitIdObject>(object: T) {
    const stringId = object.id?.toString() ?? '';
    const currentItem = this.draftCache.get(stringId);
    if (currentItem) {
      currentItem.data = object;
      currentItem.parameters.freshnessDate = SDate.new();
      this.draftCache.set(stringId, currentItem);
    }
  }

  deleteDraft(id: string | number) {
    this.draftCache.delete(id);
  }

  getFile(location: 'system' | 'tenant', fileName: string): Observable<Blob | undefined> {
    let url: string | null = null;
    const encodedFileName = fileName;
    if (location === 'system') {
      url = `${this.baseUrl}Files/System`;
    } else {
      url = `${this.baseUrl}Files/Tenant`;
    }
    if (!url) return of(undefined);
    const fullUrl = `${url}?fileName=${encodedFileName}`;
    //console.log(`Getting File: ${fullUrl}`);
    return this.http.get(fullUrl, {
      responseType: 'blob'
    });
  }

  getCustomFile(api: string, parameters?: string): Observable<Blob> | null {
    const url = `${this.baseUrl}${api}`;
    return parameters
      ? this.http.get(`${url}?${parameters}`, {
          responseType: 'blob'
        })
      : this.http.get(`${url}`, { responseType: 'blob' });
  }
}
